import React, { useEffect, useMemo, useRef, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import ContactFormCard from "../../../../widget-v2/src/components/ContactFormCard.jsx";
import { useBot, useContactForm, useSaveContactForm } from "../../hooks/useApi";
import ContactFormBuilder from "../contacts/ContactFormBuilder";
import ContactFormDraft from "../contacts/ContactFormDraft";
import { previewFromDraft } from "../contacts/anteprimaModulo.js";
import { QuandoUsarla, useAttivazione } from "./shared";
import { BarraSalvataggio } from "./casella";
import { SPAZI } from "./brand";
import { Gruppo, Passi } from "../widget/campi";
import ChatPreview from "./ChatPreview";

const ESEMPIO_CHAT = [
  { text: "Quanto costa rifare un bagno di 6 mq? Le piastrelle le ho già." },
  {
    bot: true,
    text: "Dipende dai lavori da fare: lasciami i tuoi dati e un tecnico ti richiama con un preventivo. La tua richiesta l'ho già riassunta.",
  },
];
const ESEMPIO_RIASSUNTO =
  "Vuole un preventivo per rifare un bagno di 6 mq; le piastrelle le ha già.";

/**
 * Dettaglio dell'app "Modulo contatti": com'e' fatto il modulo, poi quando
 * proporlo. A destra il modulo com'e' in chat, con il riassunto della
 * conversazione. Un solo salvataggio per tutto: il modulo va al suo endpoint,
 * il "quando" alle integrazioni.
 * L'interruttore sta nell'intestazione della pagina.
 */
export default function ModuloContattiApp({ botId, app, settings, limits, salva, saving, onAttivazione }) {
  const { toast } = useToast();
  const { data: bot } = useBot(botId);
  const { data: formData, isLoading } = useContactForm(botId);
  const salvaModulo = useSaveContactForm(botId);

  // La bozza del modulo e la versione salvata: e' modificata finche' non sono
  // lo stesso oggetto.
  const [base, setBase] = useState(null);
  const [bozza, setBozza] = useState(null);
  useEffect(() => {
    if (formData?.form && base === null) {
      setBase(formData.form);
      setBozza(formData.form);
    }
  }, [formData, base]);

  const [quando, setQuando] = useState(settings.use_when || "");
  useEffect(() => setQuando(settings.use_when || ""), [settings.use_when]);

  const anteprima = useMemo(() => previewFromDraft(bozza || {}), [bozza]);
  const formKey = anteprima.card.fields.map((f) => `${f.id}.${f.type}`).join("|");

  // Con almeno un campo completo l'interruttore in alto accende e salva
  // tutto. salvaTutto e' definita sotto, dopo il caricamento.
  const salvaEAttiva = useRef(null);
  useAttivazione(onAttivazione, anteprima.card.fields.length > 0, () => salvaEAttiva.current?.());

  if (isLoading || !bozza) {
    return (
      <div className={`grid ${SPAZI.campiAnteprima} xl:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]`}>
        <div className="space-y-4">
          <Skeleton className="h-40 w-full rounded-[14px]" />
          <Skeleton className="h-64 w-full rounded-[14px]" />
        </div>
        <Skeleton className="h-[680px] w-full rounded-[14px]" />
      </div>
    );
  }

  const moduloModificato = bozza !== base;
  const quandoModificato = quando !== (settings.use_when || "");
  const inCorso = saving || salvaModulo.isPending;

  const salvaTutto = async (accendi = false) => {
    let avvisi = [];
    if (moduloModificato || accendi) {
      try {
        const risultato = await salvaModulo.mutateAsync({
          ...bozza,
          enabled: accendi ? true : Boolean(settings.enabled),
        });
        setBase(risultato.form);
        setBozza(risultato.form);
        avvisi = risultato.warnings || [];
      } catch (error) {
        toast({
          title: "Modulo non salvato",
          description: error?.message || "Riprova fra poco.",
          variant: "destructive",
        });
        return;
      }
    }
    if (quandoModificato && !(await salva({ contact_form: { use_when: quando } }))) return;

    const titolo = accendi
      ? "Modulo contatti attivo sull'assistente"
      : moduloModificato
        ? "Modulo salvato"
        : "Indicazioni del modulo salvate";
    toast(
      avvisi.length
        ? { title: `${titolo}, con qualche correzione`, description: avvisi.join(" ") }
        : { title: titolo }
    );
  };
  salvaEAttiva.current = () => salvaTutto(true);

  return (
    <div className={`grid items-start ${SPAZI.campiAnteprima} xl:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]`}>
      <div className="min-w-0 space-y-8">
        {settings.available === false ? (
          <div className="flex items-start gap-2.5 rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-sm text-amber-900">
              Il piano di questa organizzazione non include il modulo contatti: puoi prepararlo, ma in
              chat non comparirà finché il piano non lo prevede.
            </p>
          </div>
        ) : null}

        <Passi>
          <Gruppo
            fatto={settings.fields_count > 0}
            titolo="Com'è fatto il modulo"
            descrizione="Descrivilo a parole e preparo i campi, oppure componilo a mano."
          >
            <ContactFormDraft
              botId={botId}
              onDraft={(form, warnings) => {
                setBozza(form);
                toast({
                  title: "Bozza pronta: controllala e salva",
                  description: warnings.length ? warnings.join(" ") : undefined,
                });
              }}
              onError={(message) =>
                toast({ title: "Bozza non preparata", description: message, variant: "destructive" })
              }
            />
            <ContactFormBuilder form={bozza} onChange={setBozza} showEnabled={false} />
          </Gruppo>

          <Gruppo
            titolo="Quando proporlo"
            descrizione="Chi chiede esplicitamente di essere ricontattato o un preventivo vede il modulo in ogni caso."
            facoltativo
          >
            <QuandoUsarla
              id="quando-modulo"
              value={quando}
              onChange={setQuando}
              placeholder={settings.default_use_when}
              max={limits?.use_when_max || 600}
              examples={app.examples}
            />
          </Gruppo>
        </Passi>

        <BarraSalvataggio
          modificato={moduloModificato || quandoModificato}
          saving={inCorso}
          attiva={settings.enabled}
          puoAttivare={anteprima.card.fields.length > 0}
          onSalva={() => salvaTutto(false)}
          onSalvaEAttiva={() => salvaTutto(true)}
        />
      </div>

      <ChatPreview
        theme={bot?.widget_config?.theme}
        titolo={bot?.name}
        messaggi={ESEMPIO_CHAT}
        sotto={
          anteprima.triggerEnabled ? (
            <div className="puw2-contact-trigger">
              <span className="puw2-chip puw2-contact-trigger-btn" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 6h16v12H4z" strokeLinejoin="round" />
                  <path d="M4 7l8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {anteprima.triggerLabel}
              </span>
            </div>
          ) : null
        }
        piede={
          <>
            {anteprima.incomplete > 0 ? (
              <p>
                {anteprima.incomplete === 1
                  ? "1 campo incompleto non compare"
                  : `${anteprima.incomplete} campi incompleti non compaiono`}
                : servono l'etichetta, almeno due opzioni per le scelte e il testo per i consensi.
              </p>
            ) : null}
            {anteprima.card.fields.length === 0 ? (
              <p>Aggiungi almeno un campo per vedere il modulo completo.</p>
            ) : null}
            <p>Puoi compilarlo e inviarlo: nell'anteprima non viene registrato nessun contatto.</p>
          </>
        }
      >
        <ContactFormCard
          key={formKey}
          card={{ ...anteprima.card, summary: ESEMPIO_RIASSUNTO }}
          // Nell'anteprima il file non parte: si mostra come apparirebbe caricato.
          onUpload={({ file }) =>
            new Promise((resolve) =>
              setTimeout(() => resolve({ ok: true, upload_id: "anteprima", name: file.name, size: file.size }), 500)
            )
          }
          // L'anteprima non registra nulla: mostra solo il ringraziamento.
          onSubmit={() =>
            new Promise((resolve) =>
              setTimeout(() => resolve({ ok: true, thank_you: anteprima.card.thank_you }), 400)
            )
          }
        />
      </ChatPreview>
    </div>
  );
}
